import {useCallback, useEffect, useRef} from "react";
import type {FieldValues} from "react-hook-form";

import {
  clearFormDraft,
  FORM_DRAFT_SCHEMA_VERSION,
  loadFormDraft,
  saveFormDraft,
  type FormDraftRecord,
} from "@/src/modules/core/lib/form-draft";
import {trackFormAbandon} from "@/src/modules/core/lib/glitchtip/product-telemetry";
import {useComplianceBusyStore} from "@/src/modules/core/store/compliance-busy";

const AUTOSAVE_DEBOUNCE_MS = 800;

export type ClearFormDraft = (options?: {submitted?: boolean}) => void;

export type FormDraftHelpers = {
  clearDraft: ClearFormDraft;
  snapshotDraft: () => void;
  markSubmitted: () => void;
};

/**
 * RHF `watch(callback)` subscription only. Loosely typed so sheet-specific
 * `UseFormWatch<T>` values assign without variance errors against Record/array inference.
 */
type FormDraftWatchSubscribe = (callback: () => void) => {unsubscribe: () => void};

type UseFormDraftOptions<TFieldValues extends FieldValues = FieldValues> = {
  draftKey: string;
  moduleLabel: string;
  route: string;
  sheetId: string;
  entityId?: string | number;
  /** Global property context (`userProperty.id`) at save time — restored on route/sheet on cold start. */
  contextPropertyId?: number;
  enabled: boolean;
  /**
   * RHF `getValues` — loosely typed so `UseFormGetValues<T>` assigns without
   * overload inference collapsing `TFieldValues` to a value tuple/array.
   */
  getValues: () => FieldValues;
  serializeValues?: (values: TFieldValues) => Record<string, unknown>;
  /** Skip persisting empty / unchanged forms. */
  shouldPersist?: () => boolean;
  /** RHF `watch(callback)` — debounced autosave without re-rendering the form. */
  watch?: FormDraftWatchSubscribe;
  /** @deprecated Form abandon is not sent to GlitchTip; kept for call-site compatibility. */
  abandonTelemetry?: {feature: string; operation: string};
};

export function useFormDraft<TFieldValues extends FieldValues = FieldValues>({
  draftKey,
  moduleLabel,
  route,
  sheetId,
  entityId,
  contextPropertyId,
  enabled,
  getValues,
  serializeValues = (values) => values as Record<string, unknown>,
  shouldPersist,
  watch,
  abandonTelemetry,
}: UseFormDraftOptions<TFieldValues>) {
  const shouldPersistRef = useRef(shouldPersist);
  shouldPersistRef.current = shouldPersist;
  const hadPersistedDraftRef = useRef(false);
  const submittedRef = useRef(false);
  const enterBusy = useComplianceBusyStore((state) => state.enterBusy);
  const exitBusy = useComplianceBusyStore((state) => state.exitBusy);
  const markInput = useComplianceBusyStore((state) => state.markInput);
  const syncFormDraftDeferral = useComplianceBusyStore((state) => state.syncFormDraftDeferral);
  const isBusyRef = useRef(false);

  const evaluateBusy = useCallback(() => {
    const dirty = shouldPersistRef.current ? shouldPersistRef.current() : false;
    const hasStoredDraft = enabled && loadFormDraft(draftKey) != null;
    const busy = enabled && (dirty || hasStoredDraft);
    if (busy && !isBusyRef.current) {
      isBusyRef.current = true;
      enterBusy();
    } else if (!busy && isBusyRef.current) {
      isBusyRef.current = false;
      exitBusy();
    }
  }, [draftKey, enabled, enterBusy, exitBusy]);

  const snapshotDraft = useCallback(() => {
    if (!enabled) {
      return;
    }
    if (shouldPersistRef.current && !shouldPersistRef.current()) {
      return;
    }

    const record: FormDraftRecord = {
      schemaVersion: FORM_DRAFT_SCHEMA_VERSION,
      draftKey,
      moduleLabel,
      route,
      sheetId,
      entityId,
      contextPropertyId,
      values: serializeValues(getValues() as TFieldValues),
      savedAt: Date.now(),
    };
    saveFormDraft(record);
    hadPersistedDraftRef.current = true;
    syncFormDraftDeferral();
  }, [
    draftKey,
    moduleLabel,
    route,
    sheetId,
    entityId,
    contextPropertyId,
    enabled,
    getValues,
    serializeValues,
    syncFormDraftDeferral,
  ]);

  const markSubmitted = useCallback(() => {
    submittedRef.current = true;
  }, []);

  const clearDraft = useCallback(
    (options?: {submitted?: boolean}) => {
      if (options?.submitted) {
        submittedRef.current = true;
      }

      if (
        abandonTelemetry &&
        hadPersistedDraftRef.current &&
        !submittedRef.current
      ) {
        trackFormAbandon({
          feature: abandonTelemetry.feature,
          operation: abandonTelemetry.operation,
          sheetId,
          hadDraft: true,
        });
      }

      hadPersistedDraftRef.current = false;
      submittedRef.current = false;
      if (isBusyRef.current) {
        isBusyRef.current = false;
        exitBusy();
      }
      clearFormDraft(draftKey);
      syncFormDraftDeferral();
    },
    [abandonTelemetry, draftKey, exitBusy, sheetId, syncFormDraftDeferral],
  );

  const snapshotRef = useRef(snapshotDraft);
  snapshotRef.current = snapshotDraft;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        snapshotRef.current();
      }
    };

    const onPageHide = () => {
      snapshotRef.current();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [enabled]);

  useEffect(() => {
    evaluateBusy();
    return () => {
      if (isBusyRef.current) {
        isBusyRef.current = false;
        exitBusy();
      }
    };
  }, [evaluateBusy, exitBusy]);

  useEffect(() => {
    if (!enabled || !watch) {
      return;
    }

    let timeoutId: number | undefined;

    const subscription = watch(() => {
      markInput();
      evaluateBusy();
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      timeoutId = window.setTimeout(() => {
        snapshotRef.current();
      }, AUTOSAVE_DEBOUNCE_MS);
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [enabled, evaluateBusy, markInput, watch]);

  return {snapshotDraft, clearDraft, markSubmitted} satisfies FormDraftHelpers;
}
